import { Component, EventEmitter, Input, Output } from '@angular/core';
import { ErrorStateMatcher } from '@angular/material/core';
import { Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { Section } from 'src/app/_appCommon/base/AppInterfaceAndBaseClasses';
import { PatientEditableData, PatientService } from '../../services/patient.service';
import { hasPatientContact } from '../../util/PatientData';
import { PatientPhotoChange } from '../patient-photo-picker/patient-photo-picker.component';

/**
 * Ventana para editar los datos del paciente desde el detalle del caso. Es la misma en la
 * tienda y en el backoffice; lo único que cambia es si se puede tocar el nombre, y eso lo
 * vuelve a decidir el servidor con el permiso del usuario.
 */
@Component({
  selector: 'app-patient-edit-dialog',
  templateUrl: './patient-edit-dialog.component.html',
  styleUrls: ['./patient-edit-dialog.component.css'],
  // Dentro del asistente de pasos, Material pinta en rojo cualquier campo vacío del paso aunque
  // nadie lo haya tocado. Con el criterio normal sólo se marca al salir del campo o al intentar guardar.
  providers: [{ provide: ErrorStateMatcher, useClass: ErrorStateMatcher }]
})
export class PatientEditDialogComponent
{
  @Input() public section: Section;
  // Sólo backoffice con permiso cambia nombre y apellidos
  @Input() public canEditName: boolean = false;

  @Output() public saved: EventEmitter<void> = new EventEmitter<void>();

  public show: boolean = false;
  public patient: PatientEditableData = null;
  public saving: boolean = false;
  public cropping: boolean = false;
  public errorMessage: string = null;

  // undefined: la foto no se tocó; Blob: foto nueva; null: la quitaron
  private photoChange: Blob | null | undefined = undefined;

  constructor(private patientService: PatientService) { }

  public open(patient: PatientEditableData): void
  {
    this.patient = { ...patient, birthday: patient.birthday ? new Date(patient.birthday) : null };
    this.photoChange = undefined;
    this.errorMessage = null;
    this.cropping = false;
    this.saving = false;
    this.show = true;
  }

  get missingContact(): boolean
  {
    return !hasPatientContact(this.patient?.email, this.patient?.phone);
  }

  public onPhotoChange(change: PatientPhotoChange): void
  {
    this.photoChange = change.blob;
  }

  public close(): void
  {
    this.show = false;
  }

  public save(isFormValid: boolean): void
  {
    this.errorMessage = null;

    if (!isFormValid || this.missingContact || this.cropping || this.saving) { return; }

    this.saving = true;

    this.resolvePhotoBlobId()
      .pipe(switchMap(photoBlobId => this.patientService.update({ ...this.patient, photoBlobId }, this.section)))
      .subscribe(() =>
      {
        this.saving = false;
        this.show = false;
        this.saved.emit();
      },
      (fail) =>
      {
        this.saving = false;
        this.errorMessage = fail?.error?.message ?? 'No se pudieron guardar los datos del paciente. Intenta de nuevo.';
      });
  }

  // La foto nueva se sube hasta que guardan, para no dejar archivos sueltos si cancelan
  private resolvePhotoBlobId(): Observable<number | null>
  {
    if (this.photoChange === undefined) { return of(this.patient.photoBlobId); }
    if (this.photoChange === null) { return of(null); }

    return this.patientService.uploadPhoto(this.photoChange, this.section).pipe(map(blob => blob.id));
  }
}
